import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SDKLanguages = () => {
  const [selectedLanguage, setSelectedLanguage] = useState('javascript');

  const languages = [
    {
      id: 'javascript',
      name: 'JavaScript / Node.js',
      icon: 'Code2',
      version: 'v2.1.0',
      install: 'npm install @policyalerts/sdk',
      example: `const PolicyAlerts = require('@policyalerts/sdk');

const client = new PolicyAlerts.Client({
  apiKey: process.env.POLICY_ALERTS_API_KEY
});

// Fetch recent policy updates
async function getPolicyUpdates() {
  try {
    const policies = await client.policies.list({
      category: 'cardiovascular',
      status: 'active',
      limit: 10
    });
    
    console.log(\`Found \${policies.data.length} policies\`);
    return policies;
  } catch (error) {
    console.error('Error fetching policies:', error);
  }
}

getPolicyUpdates();`,
      features: ['Promise-based API', 'TypeScript support', 'Automatic retries', 'Request/response interceptors']
    },
    {
      id: 'python',
      name: 'Python',
      icon: 'Code2',
      version: 'v2.0.5',
      install: 'pip install policyalerts',
      example: `from policyalerts import Client

client = Client(api_key='your_api_key_here')

# Fetch recent policy updates
def get_policy_updates():
    try:
        policies = client.policies.list(
            category='cardiovascular',
            status='active',
            limit=10
        )
        
        print(f"Found {len(policies.data)} policies")
        return policies
    except Exception as e:
        print(f"Error fetching policies: {e}")

get_policy_updates()`,
      features: ['Type hints', 'Async/await support', 'Pandas integration', 'Automatic pagination']
    },
    {
      id: 'ruby',
      name: 'Ruby',
      icon: 'Code2',
      version: 'v2.0.3',
      install: 'gem install policyalerts',
      example: `require 'policyalerts'

client = PolicyAlerts::Client.new(
  api_key: ENV['POLICY_ALERTS_API_KEY']
)

# Fetch recent policy updates
def get_policy_updates
  policies = client.policies.list(
    category: 'cardiovascular',
    status: 'active',
    limit: 10
  )
  
  puts "Found #{policies.data.length} policies"
  policies
rescue StandardError => e
  puts "Error fetching policies: #{e.message}"
end

get_policy_updates`,
      features: ['Idiomatic Ruby', 'Rails integration', 'ActiveRecord support', 'Automatic serialization']
    },
    {
      id: 'java',
      name: 'Java',
      icon: 'Code2',
      version: 'v2.0.4',
      install: 'maven: com.policyalerts:sdk:2.0.4',
      example: `import com.policyalerts.Client;
import com.policyalerts.models.Policy;
import java.util.List;

public class PolicyAlertsExample {
    public static void main(String[] args) {
        Client client = new Client.Builder()
            .apiKey(System.getenv("POLICY_ALERTS_API_KEY"))
            .build();
        
        try {
            List<Policy> policies = client.policies()
                .list()
                .category("cardiovascular")
                .status("active")
                .limit(10)
                .execute();
            
            System.out.println("Found " + policies.size() + " policies");
        } catch (Exception e) {
            System.err.println("Error: " + e.getMessage());
        }
    }
}`,
      features: ['Type-safe builders', 'Spring Boot integration', 'Reactive support', 'Connection pooling']
    },
    {
      id: 'csharp',
      name: 'C# / .NET',
      icon: 'Code2',
      version: 'v2.0.2',
      install: 'dotnet add package PolicyAlerts.SDK',
      example: `using PolicyAlerts;
using System;
using System.Threading.Tasks;

class Program
{
    static async Task Main(string[] args)
    {
        var client = new PolicyAlertsClient(
            Environment.GetEnvironmentVariable("POLICY_ALERTS_API_KEY")
        );
        
        try
        {
            var policies = await client.Policies.ListAsync(
                category: "cardiovascular",
                status: "active",
                limit: 10
            );
            
            Console.WriteLine($"Found {policies.Data.Count} policies");
        }
        catch (Exception ex)
        {
            Console.WriteLine($"Error: {ex.Message}");
        }
    }
}`,
      features: ['Async/await', 'LINQ support', 'Dependency injection', 'Strong typing']
    },
    {
      id: 'go',
      name: 'Go',
      icon: 'Code2',
      version: 'v2.0.1',
      install: 'go get github.com/policyalerts/sdk-go',
      example: `package main

import (
    "fmt" "os" policyalerts"github.com/policyalerts/sdk-go"
)

func main() {
    client := policyalerts.NewClient(
        os.Getenv("POLICY_ALERTS_API_KEY"),
    )
    
    policies, err := client.Policies.List(&policyalerts.PolicyListParams{
        Category: "cardiovascular",
        Status:   "active",
        Limit:    10,
    })
    
    if err != nil {
        fmt.Printf("Error: %v\\n", err)
        return
    }
    
    fmt.Printf("Found %d policies\\n", len(policies.Data))
}`,
      features: ['Context support', 'Goroutine-safe', 'Minimal dependencies', 'Efficient memory usage']
    }
  ];

  const selectedLang = languages?.find(lang => lang?.id === selectedLanguage);

  return (
    <section className="py-12 md:py-16 lg:py-20 px-4 md:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            Official SDKs & Libraries
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Native libraries for your favorite programming language. Production-ready, well-documented, and actively maintained.
          </p>
        </div>

        <div className="mb-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {languages?.map((lang) => (
              <button
                key={lang?.id}
                onClick={() => setSelectedLanguage(lang?.id)}
                className={`px-4 md:px-6 py-3 rounded-lg font-medium text-sm md:text-base transition-all ${
                  selectedLanguage === lang?.id
                    ? 'bg-blue-600 text-white shadow-lg'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-300'
                }`}
              >
                <div className="flex items-center gap-2">
                  <Icon name={lang?.icon} size={18} />
                  <span>{lang?.name}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {selectedLang && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
            <div className="lg:col-span-2">
              <div className="bg-gray-900 rounded-lg shadow-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 md:px-6 py-3 bg-gray-800 border-b border-gray-700">
                  <div className="flex items-center gap-3">
                    <div className="flex gap-1.5">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                      <div className="w-3 h-3 rounded-full bg-green-500"></div>
                    </div>
                    <span className="text-gray-400 text-xs md:text-sm">
                      {selectedLang?.name} Example
                    </span>
                  </div>
                  <Button variant="ghost" size="xs" iconName="Copy">
                    Copy
                  </Button>
                </div>
                <div className="p-4 md:p-6 overflow-x-auto">
                  <pre className="text-green-400 text-xs md:text-sm">
                    <code>{selectedLang?.example}</code>
                  </pre>
                </div>
              </div>

              <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4 md:p-6">
                <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-3">Installation</h4>
                <div className="bg-gray-900 rounded px-4 py-3">
                  <code className="text-green-400 text-sm">{selectedLang?.install}</code>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-base md:text-lg font-semibold text-gray-900">SDK Details</h4>
                  <span className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded-full font-medium">
                    {selectedLang?.version}
                  </span>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Icon name="Download" size={16} color="var(--color-success)" />
                    <span>10K+ downloads/month</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Icon name="Star" size={16} color="var(--color-warning)" />
                    <span>4.8/5 rating</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Icon name="Shield" size={16} color="var(--color-secondary)" />
                    <span>Enterprise support</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
                <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-4">Key Features</h4>
                <ul className="space-y-3">
                  {selectedLang?.features?.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                      <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 md:p-6">
                <h4 className="text-base md:text-lg font-semibold text-gray-900 mb-4">Resources</h4>
                <div className="space-y-2">
                  <a href="#" className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors">
                    <span className="text-sm text-gray-700">Documentation</span>
                    <Icon name="ExternalLink" size={16} />
                  </a>
                  <a href="#" className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors">
                    <span className="text-sm text-gray-700">GitHub Repository</span>
                    <Icon name="ExternalLink" size={16} />
                  </a>
                  <a href="#" className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg transition-colors">
                    <span className="text-sm text-gray-700">Changelog</span>
                    <Icon name="ExternalLink" size={16} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default SDKLanguages;